import { PlatformLocation } from '@angular/common';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { ActivityDescription, ActivityReason } from '../../mru/models/activity-description.model';
import { WindowService } from '../../shared/services/window.service';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../instrumentation/instrumented-http.service';
import { FileItem, FileType, SourceProvenance } from '../models/file-item.model';
import { FormEnum } from '../models/form.model';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { OperationStream } from '../models/operation-stream.model';
import { AccessTokenConsumer, AccessTokenService } from '../services/access-token.service';
import { DeviceDetector } from '../services/device-detector.service';
import { DESIGNER_MOBILE_URL, DESIGNER_URL } from '../services/forms.constants';
import { OfficeHomeApiConfigService } from '../services/officehome-api-config.service';
import { PerformanceService } from '../services/performance.service';
import { SchedulerService } from '../services/scheduler.service';
import { UpdateContentService } from '../services/update-content.service';
import { UrlAuthenticatorService } from '../services/url-authenticator.service';
import { XhrServiceError } from '../services/xhr.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { UserContextService } from '../shared-context/user-context.service';
import { HtmlEscaper } from '../utilities/html-escaper.utility';
import { ObservableHelper } from '../utilities/observable-helper.utility';
import { MruBaseService } from './mru-base.service';
import { MruConfigService } from './mru-config.service';
import { MruDataItem } from './mru-data-item.model';
import { MruData, MruDataService } from './mru-data.service';

interface MruPlusPlusV2Response {
    documents: MruPlusPlusV2Items;
    places: MruPlusPlusV2Items; // do we want to use this?
    response_code: number;
    diagnostics_data: DiagnosticsData;
}

interface DiagnosticsData {
    http_status_code: number;
    total_duration: number;
    correlation_id: string;
    actions: Array<any>;
    data_center: string;
    front_end: string;
    build: string;
}

interface MruPlusPlusV2Items {
    items: Array<MruPlusPlusV2DataItem>;
}

interface MruPlusPlusV2DataItem {
    activity_info: MruPlusPlusV2ActivityInfo;
    app: string;
    category: string;
    creation_info: CreationModificationInfo;
    display_path: Array<string>;
    extension: string;
    file_size: number;
    is_pinned: boolean;
    lifecycle_info: LifecycleInfo;
    meta_data: any;
    modification_info: CreationModificationInfo;
    mru_id: string;
    onedrive_info: OneDriveInfo;
    resource_id: string;
    server_doc_id: string;
    service_info: ServiceInfo;
    sharepoint_info: SharePointInfo;
    sharing_info: SharingInfo;
    state: string;
    time_stamp: string;
    title: string;
    type: string;
    url: string;
    web_url: string;
}

interface SharingInfo {
    state: number;
}

interface LifecycleInfo {
    flags: number;
}

interface MruPlusPlusV2ActivityInfo {
    badge: ActivityInfoBadge;
}

interface ActivityInfoBadge {
    message_format: string;
    timestamp: string;
    type: DocumentActivityType;
    users: Array<ActivityUser>;
}

export enum DocumentActivityType {
    Default = 0,
    FrequentlyOpen = 1,
    RecentlyOpen = 2,
    WeeklyOpen = 3,
    SingleEditYou = 4,
    MultipleEditYouAndOthers = 5,
    SingleCommentYou = 6,
    MultipleCommentYouAndOthers = 7,
    CommentReplyYou = 8,
    SharedWithMe = 9,
    EmailAttachment = 10,
    SingleMentionYou = 11,
    MultipleMentionYouAndOthers = 12,
    CommentReplyOthers = 13,
    SnoozeExpiry = 14,
    CommentReplyToYou = 15,
    SingleEditOthers = 16,
    MultipleEditOthers = 17,
    SingleCommentOthers = 18,
    MultipleCommentOthers = 19,
    SingleMentionOthers = 20,
    MultipleMentionOthers = 21,
    Trending = 22,
    Meeting = 23,
    PeakMoment = 24,
    HighEngagement = 25,
    SingleMentionedYou = 26,
    MultipleMentionedYou = 27,
    SingleTaskActivityByYou = 28,
    MultipleTaskActivityByYou = 29,
    SingleTaskActivityByOthers = 30,
    MultipleTaskActivityByOthers = 31,
    SingleTaskActivityYouAssignee = 32,
    MultipleTaskActivityYouAssignee = 33,
    SingleTaskActivityYouCreator = 34,
    MultipleTaskActivityYouCreator = 35
    //from https://o365exchange.visualstudio.com/O365%20Core/_git/Griffin?path=%2Fsources%2Fdev%2FOfficeFeed%2Fsrc%2FPublicDataContracts%2FBondContracts%2FRecommendedDocumentsProtocol.bond&version=GBmaster
}

interface CreationModificationInfo {
    app: string;
    timestamp: string;
    user_info: ActivityUser;
}

interface ActivityUser {
    display_name: string;
    upn: string;
}

interface ServiceInfo {
    user_id: string;
    friendly_service_name: string;
    service_id: string;
}

interface OneDriveInfo {
    drive_id: string;
    drive_type: string;
    item_id: string;
    parent_reference_id: string;
}

interface SharePointInfo {
    list_id: string;
    list_item_id: number;
    list_item_unique_id: string;
    site_id: string;
    site_url: string;
    sync_resource_id: string;
    tenant_id: string;
    web_id: string;
}

interface MruPlusPlusV2RequestBody {
    category: string;
    type: string;
    app: string;
    mru_id: string;
    url: string;
}

interface MruPlusPlusV2PinBody extends MruPlusPlusV2RequestBody {
    is_pinned: boolean;
}

interface MruPlusPlusV2WriteBody extends MruPlusPlusV2RequestBody {
    extension: string;
    title: string;
    onedrive_info?: {
        drive_id: string;
        item_id: string;
    };
}

/**
 * Service that retrieves MRU documents from MRU++v2 web API.
 */
export class MruPlusPlusV2Service extends MruBaseService implements MruDataService {
    private static readonly mruPlusPlusV2ImpressionId = 'MruPlusPlusV2Impression';
    private static readonly activityRegex = new RegExp('\{([0-9]+)\}');

    // Includes data from cache, as well as latest updates retrieved via network call
    private readonly mruDataStreams: Map<string, OperationStream<MruData>>;

    // Most recent set of unfiltered MRU documents used to populate filtered sets with initial data
    private mostRecentUnfilteredDocs: MruData;
    private fetchRequestIndex = 0;

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly userContextService: UserContextService,
        private readonly mruConfigService: MruConfigService,
        platformLocation: PlatformLocation,
        globalErrorHandler: OfficeHomePageErrorHandler,
        startupConfigService: ClientStartupConfigService,
        private readonly performanceService: PerformanceService,
        private readonly updateContentService: UpdateContentService,
        scheduler: SchedulerService,
        private readonly windowService: WindowService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly urlAuthenticatorService: UrlAuthenticatorService,
        readonly deviceDetector: DeviceDetector
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, contextService, officeHomeApiConfigService, mruConfigService, platformLocation, globalErrorHandler, startupConfigService, 'v2/recent', mruConfigService.config.supportedFileTypes, scheduler);

        this.mruDataStreams = new Map<string, OperationStream<MruData>>();
        this.windowService = windowService;
    }

    public static convertMruActivityToActivityDescription(activityInfo: MruPlusPlusV2ActivityInfo): ActivityDescription {
        let formatString = activityInfo.badge.message_format;
        let match = null;
        while ((match = MruPlusPlusV2Service.activityRegex.exec(formatString)) !== null) {
            const i = match[1];
            // Passing in a function to replace to bypass special replacement patterns. See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/replace#Specifying_a_string_as_a_parameter
            formatString = formatString.replace(match[0], () => `<c${i}>${activityInfo.badge.users[i].display_name}</c${i}>`);
        }
        return {
            formatString,
            plainText: HtmlEscaper.stripTags(formatString),
            people: (activityInfo.badge.users || []).map(function (user, index) {
                return {
                    upn: user.upn,
                    tag: `c${index}`,
                    name: user.display_name
                };
            }),
            reasonType: MruPlusPlusV2Service.convertMruReasonTypeToActivityReason(activityInfo.badge.type),
            timestamp: activityInfo.badge.timestamp ? new Date(activityInfo.badge.timestamp) : null
        };
    }

    public static convertMruReasonTypeToActivityReason(type: DocumentActivityType): ActivityReason {
        switch (type) {
            case DocumentActivityType.FrequentlyOpen:
            case DocumentActivityType.RecentlyOpen:
            case DocumentActivityType.WeeklyOpen:
                return ActivityReason.Open;
            case DocumentActivityType.SingleEditYou:
            case DocumentActivityType.MultipleEditOthers:
            case DocumentActivityType.MultipleEditYouAndOthers:
            case DocumentActivityType.SingleEditOthers:
                return ActivityReason.Edit;
            case DocumentActivityType.SingleCommentYou:
            case DocumentActivityType.SingleCommentOthers:
            case DocumentActivityType.MultipleCommentOthers:
            case DocumentActivityType.MultipleCommentYouAndOthers:
            case DocumentActivityType.CommentReplyYou:
            case DocumentActivityType.CommentReplyOthers:
            case DocumentActivityType.CommentReplyToYou:
                return ActivityReason.Comment;
            case DocumentActivityType.SharedWithMe:
                return ActivityReason.Share;
            case DocumentActivityType.EmailAttachment:
                return ActivityReason.Attachment;
            case DocumentActivityType.SingleMentionYou:
            case DocumentActivityType.MultipleMentionYouAndOthers:
            case DocumentActivityType.SingleMentionOthers:
            case DocumentActivityType.MultipleMentionOthers:
            case DocumentActivityType.SingleMentionedYou:
            case DocumentActivityType.MultipleMentionedYou:
                return ActivityReason.Mention;
            case DocumentActivityType.SnoozeExpiry:
                return ActivityReason.SnoozeExpiry;
            case DocumentActivityType.Trending:
            case DocumentActivityType.PeakMoment:
            case DocumentActivityType.HighEngagement:
                return ActivityReason.Trending;
            case DocumentActivityType.Meeting:
                return ActivityReason.Meeting;
            case DocumentActivityType.SingleTaskActivityByYou:
            case DocumentActivityType.MultipleTaskActivityByYou:
            case DocumentActivityType.SingleTaskActivityByOthers:
            case DocumentActivityType.MultipleTaskActivityByOthers:
            case DocumentActivityType.SingleTaskActivityYouAssignee:
            case DocumentActivityType.MultipleTaskActivityYouAssignee:
            case DocumentActivityType.SingleTaskActivityYouCreator:
            case DocumentActivityType.MultipleTaskActivityYouCreator:
                return ActivityReason.Task;
            default:
                return ActivityReason.Default;
        }
    }

    /**
     * Retrieve MRU documents. Note that success will be cached so subsequent calls will not make the request again. Failed requests are not cached so
     * the caller can issue a retry with this same method call.
     */
    public getMruDocuments(types?: Set<FileType>): OperationStream<MruData> {
        const url = this.getUrl(types);
        const filtered = !!(types && types.size);

        if (this.mruDataStreams.has(url)) {
            return this.mruDataStreams.get(url);
        }

        // If a types param is provided, attempt to return filtered set of documents previously fetched
        let existingDocsOfType: Array<MruDataItem>;
        if (filtered && this.mostRecentUnfilteredDocs && this.mostRecentUnfilteredDocs.items) {
            existingDocsOfType = this.mostRecentUnfilteredDocs.items.filter(doc => types.has(FileItem.fileTypeNameToFileType(doc.FileType)));
        }

        const mruFromCache: Promise<MruData> = (existingDocsOfType && existingDocsOfType.length) ? Promise.resolve({
            items: existingDocsOfType,
            impressionId: MruPlusPlusV2Service.mruPlusPlusV2ImpressionId,
            fromCache: true
        }) : this.fetchMru(url, FetchStrategy.CacheOnly, filtered);

        const mruFromNetwork = this.capabilitiesService.capabilities.prefetchDataEnabled ? this.prefetchMru(filtered, url) : this.fetchMru(url, FetchStrategy.NetworkFirst, filtered);

        // TODO: Use MRU++ 'knowledge' to reduce update response size
        const updateStream = this.updateContentService.getOrAddUpdateStream(this.getUpdateStreamKey(url), () => this.fetchMru(url, FetchStrategy.NetworkFirst, filtered));

        const mruDataStream = ObservableHelper.createResultsWithCacheObservable(
            mruFromCache,
            mruFromNetwork,
            updateStream,
            _ => this.mruDataStreams.delete(url), // clear the cached stream on initial failure so retry is possible
            {
                items: [],
                impressionId: MruPlusPlusV2Service.mruPlusPlusV2ImpressionId,
                fromCache: false
            }
        );

        this.mruDataStreams.set(url, mruDataStream);

        // Call S2S API to measure perf. Don't block on or process result to minimize impact on page load.
        if (this.mruConfigService.config.silentS2SFlow) {
            const s2sUrl = this.getS2SUrl(types);
            this.fetchMru(s2sUrl, FetchStrategy.NetworkOnly, filtered, true);
        }

        return mruDataStream;
    }

    public updateMruDocuments(types?: Set<FileType>): Promise<void> {
        return this.updateContentService.pushUpdate(this.getUpdateStreamKey(this.getUrl(types)));
    }

    public removeDocument(item: MruDataItem): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const deleteBody: MruPlusPlusV2RequestBody = {
                category: item.Category,
                type: item.MruV2Type,
                app: item.Application,
                mru_id: item.MruId,
                url: item.Url
            };

            const promiseCallback = (promise: InstrumentedHttpPromise<boolean>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    })
                    .then(() => resolve(null));
            };

            this.delete(KnownInstrumentationEvents.MruDelete, KnownRequestInstrumentationEvents.MruDelete, this.serviceBaseUrl, deleteBody, promiseCallback, reject, this.getNewRequestConfig(true));
        });
    }

    public updatePinnedState(item: MruDataItem): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const postRequestBody: MruPlusPlusV2PinBody = {
                category: item.Category,
                type: item.MruV2Type,
                app: item.Application,
                mru_id: item.MruId,
                url: item.Url,
                is_pinned: item.IsPinned
            };

            const promiseCallback = (promise: InstrumentedHttpPromise<boolean>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    })
                    .then(() => resolve(null));
            };

            this.post(KnownInstrumentationEvents.MruUpdatePin, KnownRequestInstrumentationEvents.MruUpdatePin, this.serviceBaseUrl, postRequestBody, promiseCallback, reject, this.getNewRequestConfig(true));
        });
    }

    public writeMruDocument(item: MruDataItem): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const writeBody: MruPlusPlusV2WriteBody = {
                category: item.Category,
                type: item.MruV2Type,
                app: item.Application,
                mru_id: item.MruId,
                url: item.Url,
                extension: FileItem.getFileExtensionFromUrl(item.Url),
                title: item.Title,
                onedrive_info: {
                    drive_id: item.DriveId,
                    item_id: item.DocId
                }
            };

            const promiseCallback = (promise: InstrumentedHttpPromise<boolean>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                    })
                    .then(() => resolve(null));
            };

            this.post(KnownInstrumentationEvents.MruWrite, KnownRequestInstrumentationEvents.MruWrite, this.serviceBaseUrl, writeBody, promiseCallback, reject, this.getNewRequestConfig(true));
        });
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        if (paramNameInLowerCase === 'mockmru' && this.mockRequestHeader) {
            requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }

    protected isAuthError(status: number): boolean {
        // IMPORTANT: OPTIONS call to MruPlusPlus service checks Authorization header and if it fails validation (e.g. tokes has expired)
        // then OPTIONS call returns 401 which in turn returned back by httpClient as -1
        return super.isAuthError(status, AccessTokenConsumer.MruPlusPlus) || status === -1;
    }

    private static isPwaProject(item: MruPlusPlusV2DataItem): boolean {
        return (item.meta_data && item.meta_data.type === 'PJO') && (item.app.toLowerCase() === FileType[FileType.Project].toLowerCase());
    }

    private static fileType(item: MruPlusPlusV2DataItem): FileType {
        let fileType = FileItem.fileTypeFromExtension(item.extension);

        if (!fileType || fileType === FileType.None) {
            fileType = item.app === 'Project' && item.meta_data && item.meta_data.type === 'ProjectOperations' ?
                FileType.ProjectOperations : FileItem.fileTypeNameToFileType(item.app);
        }
        return fileType;
    }

    private getUrl(types?: Set<FileType>) {
        // TODO: move s2s URL to base class when all APIs are supported
        if (this.startupConfigService.config.sharedClientStartupContext.useMruS2SFlow) {
            return this.getS2SUrl(types);
        }

        const queryParams = this.requestParams(this.mruConfigService.config.mruNumItemsToRequest, types);
        return `${this.serviceBaseUrl}/docs?${queryParams}`;
    }

    private getS2SUrl(types?: Set<FileType>) {
        const queryParams = this.requestParams(this.mruConfigService.config.mruNumItemsToRequest, types);
        return `${this.contextService.sharedContext.officeHomeApiRoot}/api/mru?${queryParams}`;
    }

    private getUpdateStreamKey(url: string) {
        return `mruplusplusv2-${url}` ;
    }

    private fetchMru(serviceUrl: string, strategy: FetchStrategy, filtered: boolean, isSilentS2S: boolean = false): Promise<MruData> {
        return new Promise<MruData>((resolve, reject) => {
            if (!this.userContextService.user.mruAvailable) {
                reject(new XhrServiceError(null, 'User does not have an MRU'));
                return;
            }

            const promiseCallback = (promise: InstrumentedHttpPromise<MruPlusPlusV2Response>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        params[InstrumentationParam.Index] = (this.fetchRequestIndex++).toString();
                        params[InstrumentationParam.ImpressionId] = MruPlusPlusV2Service.mruPlusPlusV2ImpressionId;

                        const headers = result.headers;
                        if (headers) {
                            params[InstrumentationParam.PartnerGeo] = headers.get('X-OfficeCluster');
                        }

                        if (result.data && result.data.diagnostics_data && typeof result.data.diagnostics_data.total_duration !== 'undefined') {
                            params[InstrumentationParam.MruDuration] = result.data.diagnostics_data.total_duration.toString();
                        }

                        const mruTimingEntries = this.performanceService.getResourceTimingEntries(serviceUrl);
                        if (mruTimingEntries && mruTimingEntries.length) {
                            const lastEntry = mruTimingEntries[mruTimingEntries.length - 1];
                            if (lastEntry.workerStart && lastEntry.responseStart) {
                                const timeSpentInWorkerInMilliseconds = lastEntry.responseStart - lastEntry.workerStart;
                                params[InstrumentationParam.ServiceWorkerProcessingLatency] = Math.ceil(timeSpentInWorkerInMilliseconds).toFixed();
                            }
                        }

                        // Add additional instrumentation parameters upon successful response
                        if (result.ok && result.data) {
                            let docs: Array<MruPlusPlusV2DataItem>;
                            if (result.data.documents && result.data.documents.items) {
                                docs = result.data.documents.items;
                            } else {
                                docs = [];
                            }

                            const itemsCount = docs.length;
                            if (this.mruConfigService.config.mruPinEnabled && this.mruConfigService.config.recommendedEnabled) {
                                docs.sort((a, b) => new Date(b.time_stamp).getTime() - new Date(a.time_stamp).getTime());
                            }

                            params[InstrumentationParam.ActualSize] = itemsCount.toString();
                            params[InstrumentationParam.Distribution] = FileItem.distributionFromFileTypes(docs.map(item => MruPlusPlusV2Service.fileType(item)));
                            params[InstrumentationParam.MruPlusPlusV2MetaDataDistribution] = docs.map(d => this.checkMetaData(d) ? 1 : 0).toString();
                            if (this.mruConfigService.config.recommendedEnabled) {
                                params[InstrumentationParam.ResourceIdDistribution] = docs.slice(0, 12).map(d => d.resource_id).toString();
                            }
                        }
                    })
                    .then(({ data, fromCache }) => {
                        if (!isSilentS2S) {
                            const mruData = this.processMruData(data, filtered, fromCache);
                            resolve(mruData);
                        } else {
                            resolve();
                        }
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.hJU2J);
                    });
            };

            let token: AccessTokenConsumer;
            let requestConfig: HttpRequestOptions;
            let eventName: KnownInstrumentationEvents;
            let otelEventName: KnownRequestInstrumentationEvents;
            if (this.startupConfigService.config.sharedClientStartupContext.useMruS2SFlow || isSilentS2S) {
                token = AccessTokenConsumer.None;
                requestConfig = {}; // Use an empty config since MRU++ headers aren't needed for S2S call
                eventName = KnownInstrumentationEvents.MruS2SRequest; // TODO: remove this and go back to MruRequest once silentS2S flight is complete.
                otelEventName = KnownRequestInstrumentationEvents.MruS2SRequest;
            } else {
                token = AccessTokenConsumer.MruPlusPlus;
                requestConfig = this.getNewRequestConfig(false);
                eventName = KnownInstrumentationEvents.MruRequest;
                otelEventName = KnownRequestInstrumentationEvents.MruRequest;
            }

            this.get<MruPlusPlusV2Response>(eventName, otelEventName, serviceUrl, promiseCallback, reject, requestConfig, strategy, null, null, null, token);
        });
    }

    private prefetchMru(filtered: boolean, url: string): Promise<MruData> {
        if (this.windowService.nativeWindow.prefetchMruDataPromise) {
                return new Promise<MruData>(resolve => {
                    this.windowService.nativeWindow.prefetchMruDataPromise.then(mruResponse => {
                        const mruData = this.processMruData(mruResponse, filtered, false);
                        resolve(mruData);
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.hJU2J);
                    });
                });
        } else {
            return this.fetchMru(url, FetchStrategy.NetworkFirst, filtered);
        }
    }

    private checkMetaData(document: MruPlusPlusV2DataItem): boolean {
        return document.onedrive_info &&
            !!document.onedrive_info.drive_id &&
            !!document.onedrive_info.item_id &&
            document.creation_info && !!document.creation_info.timestamp &&
            document.sharepoint_info && !!document.sharepoint_info.site_url;
    }

    private mruItemFromMruPlusPlusV2Item(item: MruPlusPlusV2DataItem): MruDataItem {
        const mruConfig = this.mruConfigService.config;
        const itemTimestamp = new Date(item.time_stamp);
        const swayAttributionEnabled = mruConfig.swayTransformedFromWordAttributionEnabled;
        item.onedrive_info = item.onedrive_info || ({} as OneDriveInfo);
        item.sharepoint_info = item.sharepoint_info || ({} as SharePointInfo);
        item.creation_info = item.creation_info || ({} as CreationModificationInfo);
        item.meta_data = item.meta_data || {};
        const metaData = {
            DriveId: item.onedrive_info.drive_id,
            ItemId: item.onedrive_info.item_id,
            CreatedByUserEmail: item.creation_info.user_info && item.creation_info.user_info.upn,
            CreatedByUserName: item.creation_info.user_info && item.creation_info.user_info.display_name,
            CreatedDate: item.creation_info.timestamp,
            SPListId: item.sharepoint_info.list_id,
            SPListItemUniqueId: item.sharepoint_info.list_item_unique_id,
            SPSiteId: item.sharepoint_info.site_id,
            SPSiteUrl: item.sharepoint_info.site_url,
            SPWebId: item.sharepoint_info.web_id,
            SwayMetaData: {
                ThumbnailId: item.meta_data['SwayThumbnail'],
                IsTransformedFromWord: swayAttributionEnabled && !!item.meta_data['SwayIsTransformedFromWord'] && item.meta_data['SwayIsTransformedFromWord'].toString().toLowerCase() === 'true'
            },
            FormsMetaData: {
                category: item.meta_data['category'],
                form_type: item.meta_data['form_type'],
                owner_id: item.meta_data['owner_id'],
                owner_name: item.meta_data['owner_name'],
                owner_tenant_id: item.meta_data['owner_tenant_id'],
                owner_type: item.meta_data['owner_type'],
                primary_color: item.meta_data['primary_color'],
                background_image: item.meta_data['background_image']
            },
            PowerBIMetaData: {
                breadcrumb_url: item.meta_data['breadcrumb_url']
            }
        };

        const mruActivityEnabled = mruConfig.mruActivityEnabled;

        // Prepend friendly root name to display path
        const friendlyRoot = item.service_info ? item.service_info.friendly_service_name : '';
        if (friendlyRoot) {
            item.display_path = item.display_path || [];
            item.display_path.unshift(friendlyRoot);
        }

        let useWebUrl = false;
        const fileType = MruPlusPlusV2Service.fileType(item);
        if (fileType === FileType.MeetingNotes || fileType === FileType.Whiteboard) {
            useWebUrl = true;
        }

        return {
            DocId: metaData.ItemId,
            DriveId: metaData.DriveId,
            Extension: item.extension,
            FileName: item.title,
            Title: item.title, // V2 doesn't have the extension in the title so don't try stripping it out since that would create incorrect titles for filenames with a '.' in them.
            Url: useWebUrl ? item.web_url : item.url,
            WebUrl: item.web_url,
            LastAccessedTime: itemTimestamp,
            FileType: FileType[fileType],
            Provenance: SourceProvenance[SourceProvenance.OfficeMru],
            PathArray: item.display_path,
            IsPinned: item.is_pinned,
            Application: item.app,
            ResourceId: item.resource_id,
            Category: item.category,
            MruV2Type: item.type,
            MruId: item.mru_id,
            UrlAlreadyEncoded: true,
            MetaData: metaData,
            Activity: item.activity_info && mruActivityEnabled ? MruPlusPlusV2Service.convertMruActivityToActivityDescription(item.activity_info) : undefined,
            Sharing: item.sharing_info && mruActivityEnabled ? item.sharing_info.state : undefined,
            LifecycleInfo: item.lifecycle_info && mruActivityEnabled ? !!item.lifecycle_info.flags : undefined,
            ServiceId: item.service_info ? item.service_info.service_id : '',
            ServerDocId: item.server_doc_id
        };
    }

    private convertToMruData(data: MruPlusPlusV2Response, fromCache = false): MruData {
        // TODO: Temporary filter has been added to remove documents with query params as this causes issues when formatting paths.
        // We can remove the filter when the MRU++ service pushes a fix on their side.
        // MRU++ is allowing Sway, Projects, and Portfolio documents with query params, and since we don't use the path array, we can allow them here.
        // ^ is this still an issue with MRU++V2?
        let rawItems: Array<MruPlusPlusV2DataItem>;
        if (data.documents && data.documents.items) {
            rawItems = data.documents.items;
        } else {
            rawItems = [];
        }

        // We are filtering out PWA Projects as requested.
        let items = rawItems.filter(item => (item.app === 'Sway' || item.app === 'Portfolio' || item.app === 'Project' || item.app === 'Forms' || item.app === 'Bohemia' || item.app === 'Fluid' || item.app === 'PowerBI' || item.url.lastIndexOf('?') === -1) && !MruPlusPlusV2Service.isPwaProject(item)).map(item => this.mruItemFromMruPlusPlusV2Item(item));
        const isATouchDevice = this.deviceDetector.isMobileDevice() || this.deviceDetector.isTabletDevice();
        items.forEach( i => {
            if (FileType[i.FileType] === FileType.Forms) {
                i.Url = this.urlAuthenticatorService.appendPvrAndUpnAuthParams(i.Url);
                if (isATouchDevice) {
                    i.Url = i.Url.replace(DESIGNER_URL, DESIGNER_MOBILE_URL);
                }
            }
        });

        // Forms Survey is apart of Forms Pro and will be removed from the forms platform in august. We are just going to hide it now because
        // it will be removed anyway.
        items = items.filter(i => {
            if (FileType[i.FileType] === FileType.Forms && i.MetaData?.FormsMetaData?.form_type?.toLowerCase() === FormEnum.Survey) {
                return false;
            } else {
                return true;
            }
        });

        // Need to sort by date if we are getting the pinned documents since the service doesn't sort them by date.
        if (this.mruConfigService.config.mruPinEnabled) {
            items.sort((a, b) => b.LastAccessedTime.getTime() - a.LastAccessedTime.getTime());
        }

        return {
            items,
            impressionId: MruPlusPlusV2Service.mruPlusPlusV2ImpressionId,
            fromCache
        };
    }

    private processMruData(data: MruPlusPlusV2Response, filtered: boolean, fromCache = false): MruData {
        const mruData = this.convertToMruData(data, fromCache);
        if (!filtered) {
            this.mostRecentUnfilteredDocs = mruData;
        }
        return mruData;
    }
}
